import { AxiosInstance } from 'axios'
import { BaseHTTP } from './base.http'
import {
  PayloadLinkIntentObject,
  LinkIntentObject,
  ObjectLinkToken,
  Movement,
  SumaryMovement,
  BankTransactionFilters,
} from '../models/Bank';
import { TotalDeudasPagos } from '../models/Invoices';

type CreateInitialBalancePayload = {
  bankId: number
  initialBalance: number
  effectiveDate: string
}

type CreateInitialBalanceRequestPayload =
  | CreateInitialBalancePayload
  | CreateInitialBalancePayload[]

type UpdateInitialBalancePayload = {
  initialBalance: number
}

type ConciliationActivationType = 'auto' | 'no-auto'

export interface CreditCardCatalogItem {
  id: number
  bankId: number
  bankName: string
  last4Digits: string
  currency: string
}

type CreateCreditCardPayload = {
  bankId: number
  last4Digits: string
  currencyId: number
}

export class BankAPI {

  private http !: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  getWidgetToken(payload: PayloadLinkIntentObject) {
    const path = '/v1/business/bank/intent-object-link'
    return this.http.post<LinkIntentObject>(path, payload)
  }

  sendExchangeTokenToBackend(exchangeToken: string, businessId: number) {
    const path = `/v1/business/bank/link-token?exchangeToken=${exchangeToken}&businessId=${businessId}`
    return this.http.get<ObjectLinkToken>(path)
  }

  getListMovements(accountId: string, linkToken: string, id: number, idSql: number, idIsNew: string) {
    const path = `/v1/business/bank/${id}/${idSql}/movements-single?linkToken=${linkToken}&accountId=${accountId}&idIsNew=${idIsNew}`
    return this.http.get<Movement[]>(path)
  }

  postBank(payload, id) {
    const path = `v1/business/${id}/bank-account`
    return this.http.post(path, payload)
  }

  // postAcount(payload, id, idAccount) {
  //   const path = `v1/producto-bancario/business/${id}/bank-account/${idAccount}/products`
  //   return this.http.post(path, payload)
  // }

  // getTotalDeudasPagos(id) {
  //   const path = `v1/business/${id}/pagar-cobrar`
  //   return this.http.get(path)
  // }

  getListBank(id) {
    const path = `v1/business/${id}/bank-account`
    return this.http.get(path)
  }

  getBanks(businessId: number) {
    const path = `v1/business/${businessId}/bank-account/banks`
    return this.http.get(path)
  }

  getCreditCards(businessId: number) {
    const path = `v1/business/${businessId}/bank-account/credit-cards`
    return this.http.get<CreditCardCatalogItem[]>(path)
  }

  createCreditCard(businessId: number, payload: CreateCreditCardPayload) {
    const path = `v1/business/${businessId}/bank-account/credit-cards`
    return this.http.post<CreditCardCatalogItem>(path, payload)
  }

  createInitialBalance(businessId: number, payload: CreateInitialBalanceRequestPayload) {
    const path = `v1/business/${businessId}/bank-account/initial-balances`
    return this.http.post(path, payload)
  }

  updateInitialBalance(
    businessId: number,
    initialBalanceId: number,
    payload: UpdateInitialBalancePayload
  ) {
    const path = `v1/business/${businessId}/bank-account/initial-balances/${initialBalanceId}`
    return this.http.patch(path, payload)
  }

  getInitialBalances(businessId: number, bankId?: number) {
    const path = `v1/business/${businessId}/bank-account/initial-balances`
    return this.http.get(path, {
      params: bankId ? { bankId } : undefined
    })
  }

  deleteInitialBalance(businessId: number, initialBalanceId: number) {
    const path = `v1/business/${businessId}/bank-account/initial-balances/${initialBalanceId}`
    return this.http.delete(path)
  }

  deleteBankAccount(businessId: number, accountId: number) {
    const path = `v1/business/${businessId}/bank-account/${accountId}`
    return this.http.delete(path)
  }

  getListAcountBank(id) {
    const path = `v1/producto-bancario/business/${id}`
    return this.http.get(path)
  }

  getDataLibros(businessId, comprobanteId) {
    const path = `v1/business/${businessId}/libros-contables/${comprobanteId}?centrocosto=${true}`
    return this.http.get(path)
  }

  /**
   * Obtiene el total de deudas y pagos de la empresa actual.
   * @param businessId ID de la empresa para filtrar los datos.
   * @returns Una promesa con los datos de deudas y pagos.
   */
  getTotalDeudasPagos(businessId: string) {
    const path = `/v1/business/${businessId}/pagar-cobrar`; // Ruta con parámetro de negocio
    return this.http.get<TotalDeudasPagos>(path); // Solicitud GET con respuesta tipada
  }

  getPermisos(businessId) {
    const path = `/v1/bank/business/${businessId}/limit-quest`; // Ruta con parámetro de negocio
    return this.http.get(path)
  }

  getTraspasoCuenta(businessId, comprobanteId) {
    const path = `/v1/business/${businessId}/movimiento-traspaso/${comprobanteId}`; // Ruta con parámetro de negocio
    return this.http.get(path)
  }

  setAutomaticConciliationState(
    businessId: number,
    type: ConciliationActivationType,
    date: string,
    modificar: boolean
  ) {
    const path = `/v1/business/${businessId}/activar/${type}`
    return this.http.post(path, undefined, {
      params: { date, modificar }
    })
  }

  /**
   * Obtiene el resumen de movimientos de una cuenta bancaria específica o de todas.
   *
   * @param productId - ID del producto bancario (si es 0, no se filtra por producto).
   * @param businessId - ID del negocio.
   * @param limit - Límite de registros a recuperar.
   * @param year - Año de los movimientos.
   * @param month - Mes de los movimientos.
   * @returns Lista de movimientos resumidos.
   */
  async getTransactionsSummary(
    productId: number,
    businessId: number,
    limit: number,
    year: number,
    month: number | undefined,
    page: number,
    filters: BankTransactionFilters = {}
  ): Promise<SumaryMovement[] | { libros: SumaryMovement[], count: number }> {
    // Construcción dinámica de la URL
    const queryParams = {
      year,
      limit,
      page,
      ...(typeof month === 'number' ? { month } : {}),
      ...filters
    }
    const path = `v1/business/${businessId}/producto-bancario/${productId}/summary-movements`;
    try {
      // Realizar solicitud GET
      const response = await this.http.get<SumaryMovement[] | { libros: SumaryMovement[], count: number }>(path, { params: queryParams });
      return response.data;
    } catch (error: any) {
      console.error(
        `Error al obtener movimientos (productoId=${productId}, businessId=${businessId}):`,
        error.response?.data || error.message
      );
      throw error;
    }
  }

  async getMercadoPagoTransactionsSingle(
    businessId: number,
    accountIdSql: number,
    organizationId: number,
    month?: number,
    year?: number,
    beginDate?: string,
    endDate?: string
  ): Promise<Movement[]> {
    const queryParams = new URLSearchParams()
    queryParams.append('organizationId', organizationId.toString())
    if (month) queryParams.append('month', month.toString())
    if (year) queryParams.append('year', year.toString())
    if (beginDate) queryParams.append('beginDate', beginDate)
    if (endDate) queryParams.append('endDate', endDate)

    const path = `/v1/business/bank/mercadopago/businessId/${businessId}/${accountIdSql}/transactions-single?${queryParams.toString()}`

    try {
      const response = await this.http.get<Movement[]>(path)
      return response.data
    } catch (error: any) {
      console.error(
        `Error al obtener movimientos de MercadoPago (businessId=${businessId}, accountIdSql=${accountIdSql}):`,
        error.response?.data || error.message
      )
      throw error
    }
  }

}
